import { DateTime } from "luxon";
import { useCallback, useState, type JSX } from "react";
import type { CellUnit } from "../range-selection";
import { CaptureDateList } from "../timeline/capture-date-list";
import { useSelectableBounds } from "./calendar-utils";
import {
  CalendarViewProps,
  CaptureGranularity,
  CaptureLayout,
} from "./capture-calendar-types";
import { DayCalendar } from "./day-calendar";
import { MonthGrid } from "./month-grid";

type CaptureCalendarProps = Pick<
  CalendarViewProps,
  "stats" | "startDate" | "endDate" | "onPick" | "onHover"
> &
  Partial<Pick<CalendarViewProps, "isPickable">> & {
    /** The month the calendar opens on */
    initialMonth: DateTime;
    /** Whether to list the capture dates or show a calendar */
    layout: CaptureLayout;
    /** Whether a month click only navigates to choose an endpoint's day */
    isEditing?: boolean;
  };

/**
 * @returns The capture dates as a timeline list, or as a calendar of days or months that
 * opens on months and switches view from its header
 */
export function CaptureCalendar({
  initialMonth,
  layout,
  isPickable = () => true,
  isEditing,
  ...rest
}: CaptureCalendarProps): JSX.Element {
  const [month, setMonth] = useState(initialMonth);
  const [granularity, setGranularity] = useState(CaptureGranularity.month);
  const bounds = useSelectableBounds(rest.stats);
  const onToggleView = useCallback(
    () =>
      setGranularity((current) =>
        current === CaptureGranularity.day
          ? CaptureGranularity.month
          : CaptureGranularity.day,
      ),
    [],
  );

  const { onPick } = rest;
  const onMonthPick = useCallback(
    (date: DateTime, unit: CellUnit, isShiftHeld: boolean) => {
      if (!isEditing) onPick(date, unit, isShiftHeld);
      setMonth(date);
      setGranularity(CaptureGranularity.day);
    },
    [isEditing, onPick],
  );

  const props: CalendarViewProps = {
    ...rest,
    isPickable,
    month,
    onMonthChange: setMonth,
    bounds,
    onToggleView,
  };

  if (layout === CaptureLayout.list) return <CaptureDateList {...props} />;

  if (granularity === CaptureGranularity.day) return <DayCalendar {...props} />;

  return <MonthGrid {...props} onPick={onMonthPick} />;
}
